/**
 * The browser's identity.
 *
 * Signing in is required. The server refuses an anonymous caller outright, so
 * the app shows a sign-in gate and nothing else until there is a session —
 * this used to be a promotion from a read-only board, and that read-only board
 * is gone on purpose.
 *
 * Sign-in is Microsoft only. The app is registered with both the Paragane and
 * Sentry Entra directories, and the Supabase project's Azure provider must be
 * multi-tenant — its "Azure Tenant URL" blank, or
 * https://login.microsoftonline.com/organizations — for both to work. Pinned
 * to one directory, only one of them can sign in.
 *
 * Roles are not decided here. The server owns them; this module only holds a
 * session and hands the access token to api.ts.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/**
 * False in a build with no Supabase env — the app skips the sign-in gate and
 * calls the API with no bearer token. That is only safe because it is also
 * how local dev runs: with no auth port injected, the server falls back to a
 * dev admin rather than resolving a caller from the (absent) token.
 *
 * Also false outside a browser. `localStorage` is a global that only exists in
 * one, and reading it at module scope made importing anything that touches
 * api.ts throw under vitest — so the app's own tests could not import the app.
 */
const inBrowser = typeof localStorage !== "undefined";
export const authConfigured = Boolean(URL && KEY) && inBrowser;

const client: SupabaseClient | null = authConfigured
  ? createClient(URL!, KEY!, {
      auth: { storage: localStorage, persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

export interface SessionUser {
  email: string | null;
}

let token: string | null = null;
let user: SessionUser | null = null;
const listeners = new Set<(u: SessionUser | null) => void>();

function publish() {
  for (const fn of listeners) fn(user);
}

if (client) {
  // Seeds the header chip. The token actually sent is fetched per call.
  void client.auth.getSession().then(({ data }) => {
    token = data.session?.access_token ?? null;
    user = data.session ? { email: data.session.user.email ?? null } : null;
    publish();
  });
  client.auth.onAuthStateChange((_e, session) => {
    token = session?.access_token ?? null;
    user = session ? { email: session.user.email ?? null } : null;
    publish();
  });
}

/**
 * The token to send, asked for at call time.
 *
 * Reading a module-level cache was wrong twice over: it is empty until
 * getSession() first resolves, and it goes stale when the hour-long token
 * expires while the tab sits idle. Both end the same way — the call goes out
 * unauthenticated or with a dead token, the server answers 401, and the app
 * looks like it silently lost permissions. getSession() refreshes when the
 * token is near expiry, so asking each time is both correct and cheap.
 */
export async function currentAccessToken(): Promise<string | null> {
  if (!client) return null;
  try {
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return token; // Offline: the last known token beats sending none.
  }
}

/** The last token seen. Synchronous, and only for code that cannot await. */
export const accessToken = (): string | null => token;

export const currentUser = (): SessionUser | null => user;

export function onSessionChange(fn: (u: SessionUser | null) => void): () => void {
  listeners.add(fn);
  fn(user);
  return () => listeners.delete(fn);
}

const RETURN_TO = "sentry.returnTo";

/**
 * Remember where the visitor was headed, across the provider redirect.
 *
 * `redirectTo` must be an allowlisted URL on the Supabase project, so it is
 * the bare origin — which drops the path, query and hash. Without this, every
 * deep link lands on the default view after signing in.
 */
export function stashReturnTo(target: string): void {
  try {
    if (!target || target === "?" || target === "#") return;
    localStorage.setItem(RETURN_TO, target);
  } catch { /* a browser refusing storage must not block sign-in */ }
}

/** The stashed destination, consumed — so a reload does not re-navigate. */
export function takeReturnTo(): string | null {
  try {
    const v = localStorage.getItem(RETURN_TO);
    localStorage.removeItem(RETURN_TO);
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

/**
 * Microsoft SSO, against the same Supabase project as the admin app — so this
 * is not a second set of accounts. Anyone who can sign in to
 * platform.paragane.com signs in here with the same credentials; what differs
 * is only what the server then lets them do.
 */
/**
 * Where the provider sends the browser back to — the origin, with a trailing
 * slash that is doing real work.
 *
 * The allowlist entries are written `https://*.paragane.ai/**`, and that
 * pattern has a literal `/` after the host, so a bare `window.location.origin`
 * — which carries no path at all — does not match any of them. Supabase does
 * not report a rejected target: it silently substitutes the project's Site URL,
 * so the whole failure presents as being dumped into platform.paragane.com
 * after a successful Microsoft sign-in, with nothing in any log to say why.
 *
 * Verified against the live project: `https://sentry-prod.paragane.com` is
 * rejected and `https://sentry-prod.paragane.com/` is kept, same host, same
 * allowlist.
 *
 * Exported for its test — `signInWithOAuth` cannot be reached without a live
 * Supabase client, and this one line is the whole of what was wrong.
 */
export function authRedirectTarget(origin: string): string {
  return origin.endsWith("/") ? origin : `${origin}/`;
}

export async function signInWithMicrosoft(): Promise<void> {
  if (!client) throw new Error("This build has no Supabase configuration, so sign-in is unavailable.");
  stashReturnTo(window.location.search + window.location.hash);
  const { error } = await client.auth.signInWithOAuth({
    provider: "azure",
    // The app has no router, so it returns to the origin and
    // `detectSessionInUrl` reads the token out of the URL. The target must be
    // on the project's redirect allowlist — when it is not, the provider
    // bounces to the project's Site URL rather than erroring, so a failed
    // sign-in looks like being dumped into a different app.
    options: {
      redirectTo: authRedirectTarget(window.location.origin),
      scopes: "email profile openid",
    },
  });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  await client?.auth.signOut();
}
